import * as React from 'react'
import { MoreHorizontal, RefreshCw, Ban, CreditCard, Banknote, RotateCcw, ExternalLink, ShieldAlert, Clock } from 'lucide-react'
import { format, isToday, isYesterday } from 'date-fns'

import type { ManualPayment, PaymentSchedule, StripePayment } from '@/gen/cortado/v1/invoices_pb'
import { PaymentRiskLevelEnum } from '@/gen/cortado/v1/settings_pb'
import {
  useInvoicePaymentsFiltered,
  combineAndSortPayments,
  formatPaymentMethod,
  formatStripePaymentMethod,
  getPaymentStatus,
  getManualPaymentStatus,
  canRefundPayment,
  canVoidPayment,
  getInFlightPaymentSchedules,
  getInFlightPaymentAmountCents,
  getInFlightPaymentInitiatedAt,
  type PaymentFilterOptions,
} from '@/stores/payments.store'
import { useInvoice } from '@/stores/invoices.store'
import { PaymentFilterToolbar } from './payment-filter-toolbar'
import { formatCurrency } from '@/lib/currency-utils'
import { isPermissionDeniedError } from '@/lib/error-reporting'
import { cn } from '@/lib/utils'

import { AccessDeniedState } from '@/components/shared/access-denied-state'
import { ActionGate } from '@/components/shared/action-gate'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface PaymentHistoryProps {
  invoiceId: string
  /** The invoice's own organization — what PaymentsManage is checked against. */
  organizationId: string
  isDraft?: boolean
  onRecordPayment: () => void
  onRefund: (payment: StripePayment) => void
  onVoid: (payment: ManualPayment) => void
}

/** Recording, refunding, or voiding payments — moving money, not authoring. */
const PAYMENTS_MANAGE = 'PaymentsManage'

/**
 * Format a date for the timeline header
 */
function formatDateHeader(date: Date): string {
  if (isToday(date)) return 'Today'
  if (isYesterday(date)) return 'Yesterday'
  return format(date, 'MMMM d, yyyy')
}

/**
 * Format time for display
 */
function formatTime(date: Date): string {
  return format(date, 'h:mm a')
}

/**
 * Group payments by date
 */
function groupPaymentsByDate(
  payments: Array<{ type: 'stripe' | 'manual'; payment: StripePayment | ManualPayment; date: Date }>
): Array<{
  date: Date
  dateLabel: string
  payments: Array<{ type: 'stripe' | 'manual'; payment: StripePayment | ManualPayment; date: Date }>
}> {
  const groups: Map<string, {
    date: Date
    payments: Array<{ type: 'stripe' | 'manual'; payment: StripePayment | ManualPayment; date: Date }>
  }> = new Map()

  payments.forEach((item) => {
    const dateKey = format(item.date, 'yyyy-MM-dd')
    if (!groups.has(dateKey)) {
      groups.set(dateKey, { date: item.date, payments: [] })
    }
    groups.get(dateKey)!.payments.push(item)
  })

  return Array.from(groups.values()).map((group) => ({
    ...group,
    dateLabel: formatDateHeader(group.date),
  }))
}

export function PaymentHistory({
  invoiceId,
  organizationId,
  isDraft = false,
  onRecordPayment,
  onRefund,
  onVoid,
}: PaymentHistoryProps) {
  const [filters, setFilters] = React.useState<PaymentFilterOptions>({})

  const { stripePayments, manualPayments, isLoading, hasLoaded, refetch, isFetching, error } =
    useInvoicePaymentsFiltered(invoiceId, filters)

  // ListInvoicePayments only returns settled rows, so an ACH payment that's
  // still clearing (3-5 business days) would otherwise appear to have vanished.
  // Derive it from the invoice's own payment schedules instead of a new query.
  const { invoice } = useInvoice(invoiceId)
  const inFlightSchedules = React.useMemo(
    () => getInFlightPaymentSchedules(invoice?.paymentSchedules),
    [invoice?.paymentSchedules]
  )

  const combinedPayments = React.useMemo(
    () => combineAndSortPayments(stripePayments, manualPayments),
    [stripePayments, manualPayments]
  )

  const groupedPayments = React.useMemo(
    () => groupPaymentsByDate(combinedPayments),
    [combinedPayments]
  )

  // A read denial on the payments list is a section the role can't open at
  // all — the lock state, not the perpetual skeleton every other error gets.
  if (isPermissionDeniedError(error)) {
    return <AccessDeniedState variant="section" noun="payments" />
  }

  // Loading skeleton
  if (isLoading || !hasLoaded) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <div className="space-y-2">
              <Skeleton className="h-20 w-full rounded-xl" />
              <Skeleton className="h-20 w-full rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Empty state
  if (combinedPayments.length === 0 && inFlightSchedules.length === 0) {
    return (
      <Empty className="rounded-xl border bg-muted/30 py-12">
        <EmptyMedia variant="icon">
          <div className="rounded-full bg-muted p-3">
            <CreditCard className="size-6 text-muted-foreground" aria-hidden="true" />
          </div>
        </EmptyMedia>
        <EmptyDescription className="text-center">
          <p className="font-medium text-foreground">No payments yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Payments will appear here once recorded
          </p>
        </EmptyDescription>
        {!isDraft && (
          <ActionGate
            organizationId={organizationId}
            permission={PAYMENTS_MANAGE}
            verb="record"
            noun="payments"
          >
            <Button variant="outline" size="sm" className="mt-4" onClick={onRecordPayment}>
              <Banknote className="size-4" aria-hidden="true" />
              Log Manual Payment
            </Button>
          </ActionGate>
        )}
      </Empty>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with filter and refresh */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium tabular-nums">
              {combinedPayments.length} payment{combinedPayments.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={cn('size-3.5', isFetching && 'animate-spin')} aria-hidden="true" />
              <span className="sr-only">Refresh payments</span>
            </Button>
          </div>
          {!isDraft && (
            <ActionGate
              organizationId={organizationId}
              permission={PAYMENTS_MANAGE}
              verb="record"
              noun="payments"
            >
              <Button variant="outline" size="sm" onClick={onRecordPayment}>
                <Banknote className="size-4" aria-hidden="true" />
                Log Manual Payment
              </Button>
            </ActionGate>
          )}
        </div>
        {/* Filter toolbar */}
        <PaymentFilterToolbar
          filters={filters}
          onFiltersChange={setFilters}
          invoiceId={invoiceId}
        />
      </div>

      {/* In-flight payments — checkout completed, not yet settled */}
      {inFlightSchedules.length > 0 && (
        <div className="space-y-2">
          {inFlightSchedules.map((schedule) => (
            <ProcessingPaymentCard key={`processing-${schedule.id}`} schedule={schedule} />
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-6">
        {groupedPayments.map((group) => (
          <div key={group.dateLabel} className="space-y-3">
            {/* Date header */}
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {group.dateLabel}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Payment cards */}
            <div className="space-y-2">
              {group.payments.map(({ type, payment, date }) => {
                if (type === 'stripe') {
                  return (
                    <StripePaymentCard
                      key={`stripe-${(payment as StripePayment).id}`}
                      payment={payment as StripePayment}
                      time={formatTime(date)}
                      organizationId={organizationId}
                      onRefund={onRefund}
                    />
                  )
                } else {
                  return (
                    <ManualPaymentCard
                      key={`manual-${(payment as ManualPayment).id}`}
                      payment={payment as ManualPayment}
                      time={formatTime(date)}
                      organizationId={organizationId}
                      onVoid={onVoid}
                    />
                  )
                }
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ProcessingPaymentCardProps {
  schedule: PaymentSchedule
}

/**
 * A payment schedule whose checkout session has completed but hasn't settled
 * into a `stripe_payments`/`manual_payments` row yet — deliberately styled
 * distinct from a settled payment so staff don't read it as money received.
 */
function ProcessingPaymentCard({ schedule }: ProcessingPaymentCardProps) {
  const amountCents = getInFlightPaymentAmountCents(schedule)
  const initiatedAt = getInFlightPaymentInitiatedAt(schedule)

  return (
    <div className="relative rounded-xl border border-dashed border-status-info-border bg-status-info-bg/40 px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="shrink-0 rounded-lg bg-status-info-bg p-2">
            <Clock className="size-4 text-status-info-fg" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">Payment Processing</span>
              <Badge variant="info">
                Not yet settled
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              {initiatedAt ? `Initiated ${format(initiatedAt, 'MMM d, yyyy')}` : 'Recently initiated'}
              {' · '}If paid by ACH, it can take 3-5 business days to clear
            </div>
          </div>
        </div>

        <div className="text-right">
          <div className="font-financial font-semibold tabular-nums text-status-info-fg">
            {formatCurrency(Number(amountCents))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface StripePaymentCardProps {
  payment: StripePayment
  time: string
  organizationId: string
  onRefund: (payment: StripePayment) => void
}

/**
 * Small colored risk indicator shown only for elevated/highest risk payments.
 * Normal-risk payments render nothing to avoid clutter.
 */
function PaymentRiskIndicator({ payment }: { payment: StripePayment }) {
  const riskLevel = payment.outcomeRiskLevel
  const isElevated = riskLevel === PaymentRiskLevelEnum.PAYMENT_RISK_LEVEL_ELEVATED
  const isHighest = riskLevel === PaymentRiskLevelEnum.PAYMENT_RISK_LEVEL_HIGHEST

  if (!isElevated && !isHighest) {
    return null
  }

  const label = isHighest ? 'Highest risk' : 'Elevated risk'
  const tooltipText =
    payment.outcomeRiskScore !== undefined
      ? `${label} payment (risk score ${payment.outcomeRiskScore})`
      : `${label} payment`

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium',
            isHighest
              ? 'bg-status-danger-bg text-status-danger-fg'
              : 'bg-status-warning-bg text-status-warning-fg'
          )}
          aria-label={tooltipText}
        >
          <ShieldAlert className="size-3" aria-hidden="true" />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  )
}

function StripePaymentCard({ payment, time, organizationId, onRefund }: StripePaymentCardProps) {
  const status = getPaymentStatus(payment)
  const canRefund = canRefundPayment(payment)
  const isRefunded = payment.refunded || payment.amountRefundedCents > 0
  // Fees are enriched asynchronously from the balance transaction; omit the
  // line entirely while they are still NULL/zero.
  const feeCents = payment.stripeFeeCents
  const hasFeeData = feeCents !== undefined && feeCents !== 0n
  const netCents = payment.netAmountCents ?? (feeCents !== undefined ? payment.amountCents - feeCents : undefined)

  return (
    <div
      className={cn(
        'relative rounded-xl border bg-card px-4 py-3',
        isRefunded && 'opacity-70'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Payment details */}
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {/* Icon */}
          <div className="shrink-0 rounded-lg bg-purple-100 p-2 dark:bg-purple-900/40">
            <CreditCard className="size-4 text-purple-600 dark:text-purple-400" aria-hidden="true" />
          </div>

          {/* Details */}
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                {formatStripePaymentMethod(payment)}
              </span>
              <Badge variant={status.variant}>
                {status.label}
              </Badge>
              <PaymentRiskIndicator payment={payment} />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Stripe Payment</span>
              <span>•</span>
              <a
                href={`https://dashboard.stripe.com/payments/${payment.stripePaymentIntentId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-1 font-mono hover:text-primary hover:underline"
              >
                {payment.stripeChargeId?.substring(0, 14)}...
                <ExternalLink className="size-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </a>
              <span>•</span>
              <span>{time}</span>
            </div>
          </div>
        </div>

        {/* Right: Amount and actions */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className={cn(
              'font-financial font-semibold tabular-nums',
              isRefunded && 'text-muted-foreground line-through'
            )}>
              {formatCurrency(Number(payment.amountCents))}
            </div>
            {payment.amountRefundedCents > 0 && (
              <div className="flex items-center justify-end gap-1 font-financial text-xs tabular-nums text-status-danger-fg">
                <RotateCcw className="size-3" aria-hidden="true" />
                -{formatCurrency(Number(payment.amountRefundedCents))}
              </div>
            )}
            {hasFeeData && netCents !== undefined && (
              <div className="font-financial text-[11px] tabular-nums text-muted-foreground">
                Fee {formatCurrency(Number(feeCents))} · Net {formatCurrency(Number(netCents))}
              </div>
            )}
          </div>

          {canRefund && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-8">
                  <MoreHorizontal className="size-4" aria-hidden="true" />
                  <span className="sr-only">Payment actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <ActionGate
                  organizationId={organizationId}
                  permission={PAYMENTS_MANAGE}
                  verb="refund"
                  noun="payments"
                >
                  <DropdownMenuItem onClick={() => onRefund(payment)}>
                    <RefreshCw className="mr-2 size-4" aria-hidden="true" />
                    Refund
                  </DropdownMenuItem>
                </ActionGate>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  )
}

interface ManualPaymentCardProps {
  payment: ManualPayment
  time: string
  organizationId: string
  onVoid: (payment: ManualPayment) => void
}

function ManualPaymentCard({ payment, time, organizationId, onVoid }: ManualPaymentCardProps) {
  const status = getManualPaymentStatus(payment)
  const canVoid = canVoidPayment(payment)
  const isVoided = payment.voided

  return (
    <div
      className={cn(
        'relative rounded-xl border bg-card px-4 py-3',
        isVoided && 'opacity-70'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Payment details */}
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {/* Icon */}
          <div className={cn(
            'shrink-0 rounded-lg p-2',
            isVoided
              ? 'bg-muted'
              : 'bg-blue-100 dark:bg-blue-900/40'
          )}>
            <Banknote
              aria-hidden="true"
              className={cn(
                'size-4',
                isVoided
                  ? 'text-muted-foreground'
                  : 'text-blue-600 dark:text-blue-400'
              )}
            />
          </div>

          {/* Details */}
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                {formatPaymentMethod(payment.paymentMethod)}
              </span>
              {payment.referenceNumber && (
                <span className="font-mono text-xs text-muted-foreground">
                  #{payment.referenceNumber}
                </span>
              )}
              <Badge variant={status.variant}>
                {status.label}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Manual Payment</span>
              {payment.recordedByUser && (
                <>
                  <span>•</span>
                  <span>
                    by {payment.recordedByUser.firstName} {payment.recordedByUser.lastName}
                  </span>
                </>
              )}
              <span>•</span>
              <span>{time}</span>
            </div>
            {payment.notes && (
              <p className="mt-1 line-clamp-1 text-xs italic text-muted-foreground">
                "{payment.notes}"
              </p>
            )}
            {isVoided && payment.voidReason && (
              <p className="mt-1 text-xs text-status-danger-fg">
                Voided: {payment.voidReason}
              </p>
            )}
          </div>
        </div>

        {/* Right: Amount and actions */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className={cn(
              'font-financial font-semibold tabular-nums',
              isVoided && 'text-muted-foreground line-through'
            )}>
              {formatCurrency(Number(payment.amountCents))}
            </div>
          </div>

          {canVoid && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-8">
                  <MoreHorizontal className="size-4" aria-hidden="true" />
                  <span className="sr-only">Payment actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <ActionGate
                  organizationId={organizationId}
                  permission={PAYMENTS_MANAGE}
                  verb="void"
                  noun="payments"
                >
                  <DropdownMenuItem
                    onClick={() => onVoid(payment)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Ban className="mr-2 size-4" aria-hidden="true" />
                    Void Payment
                  </DropdownMenuItem>
                </ActionGate>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  )
}
